import { useCallback, useEffect, useMemo, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { t } from "i18next"
import { z } from "zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { DateOnly } from "@/lib/date-only"
import { getServerDateOnly } from "@/lib/initial-time-zone"
import { BodyMeasurementForm } from "@/modules/patients/detail/health-information/body-measurement/body-measurement-form"
import { BodyMeasurementTable } from "@/modules/patients/detail/health-information/body-measurement/body-measurement-table"
import { getBodyMeasurementConnectionId } from "@/modules/patients/detail/health-information/body-measurement/lib/get-body-measurements-connection"
import { isLatestData } from "@/modules/patients/detail/health-information/body-measurement/lib/is-latest-data"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  PatientBodyMeasurementsOutputFragment$data,
  PatientBodyMeasurementsOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientBodyMeasurementsOutputFragment.graphql"
import { Ch_createOrUpdatePatientBodyMeasurementInput } from "@/modules/patients/services/ch/__generated__/chCreateOrUpdatePatientBodyMeasurementMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import { De_createOrUpdatePatientBodyMeasurementInput } from "@/modules/patients/services/de/__generated__/deCreateOrUpdatePatientBodyMeasurementMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { PatientBodyMeasurementsOutputFragment } from "@/modules/patients/services/PatientBodyMeasurementsOutputFragment"
import { useCreateOrUpdatePatientBodyMeasurementMutation } from "@/modules/patients/services/useCreateOrUpdatePatientBodyMeasurementMutation"
import { usePatientBodyMeasurement } from "@/modules/patients/services/usePatientBodyMeasurement"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

const serverDate = getServerDateOnly()

const WEIGHT_MIN = 0.5
const WEIGHT_MAX = 500
const WEIGHT_DECIMAL_PLACES = 3

const HEIGHT_MIN = 30
const HEIGHT_MAX = 250

const makeBodyMeasurementSchema = (isHeightRequired: boolean) =>
  z
    .object({
      date: z.custom<DateOnly>().transform((val, ctx) => {
        if (val.isGreaterThan(serverDate)) {
          ctx.addIssue({
            code: "custom",
            message: "common.fieldErrorList.invalidDateRange",
          })
        }
        return val
      }),
      weight: z
        .string()
        .nullable()
        .transform((value, ctx): string => {
          if (value === null || value === "") {
            ctx.addIssue({
              code: "invalid_type",
              expected: "number",
              message: t("common.fieldErrorList.requiredField"),
              received: "null",
            })
            return z.NEVER
          } else if (+value > WEIGHT_MAX || +value < WEIGHT_MIN) {
            ctx.addIssue({
              code: "custom",
              message: t("common.fieldErrorList.valueInRange", {
                min: WEIGHT_MIN,
                max: WEIGHT_MAX,
              }),
            })
            return z.NEVER
          } else if (
            value.indexOf(".") >= 0 &&
            value.substring(value.indexOf(".")).length > WEIGHT_DECIMAL_PLACES + 1
          ) {
            ctx.addIssue({
              code: "custom",
              message: t("patient.healthInformation.bodyMeasurements.fieldErrorList.decimalPlaces", {
                value: WEIGHT_DECIMAL_PLACES,
              }),
            })
            return z.NEVER
          }
          return value
        }),
      height: z
        .string()
        .nullable()
        .transform((value, ctx): string | null => {
          if (value === null || value === "") {
            if (isHeightRequired) {
              ctx.addIssue({
                code: "invalid_type",
                expected: "number",
                message: t("common.fieldErrorList.requiredField"),
                received: "null",
              })
              return z.NEVER
            }
            return null
          } else if (+value > HEIGHT_MAX || +value < HEIGHT_MIN) {
            ctx.addIssue({
              code: "custom",
              message: t("common.fieldErrorList.valueInRange", {
                min: HEIGHT_MIN,
                max: HEIGHT_MAX,
              }),
            })
            return z.NEVER
          }
          return value
        }),
      bodyMassIndex: z.number().positive().nullable(),
    })
    .required({
      date: true,
      weight: true,
    })
    .partial({
      bodyMassIndex: true,
    })

export type BodyMeasurementSchemaIn = z.input<ReturnType<typeof makeBodyMeasurementSchema>>
export type BodyMeasurementSchemaOut = z.output<ReturnType<typeof makeBodyMeasurementSchema>>

const BodyMeasurementsDrawer = ({ validAppointment }: { validAppointment: boolean }) => {
  const isEditMode = useEditMode()
  const patientId = usePatientParam()
  const country = useCountryParam()
  const appointmentId = useAppointmentParam()
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)
  const patientBodyMeasurementsRef = usePatientBodyMeasurement(patientId, country)
  const [editing, setEditing] = useState<PatientBodyMeasurementsOutputFragment$data[0] | null>()
  const [latestBodyMeasurement, setLatestBodyMeasurement] = useState<
    PatientBodyMeasurementsOutputFragment$data[0]["node"] | null
  >()

  const isHeightRequired = usePatientFileContext((state) => state.patientFileInfo?.age.isMajor) ?? false

  const patientBodyMeasurements =
    useFragment<PatientBodyMeasurementsOutputFragment$key>(
      PatientBodyMeasurementsOutputFragment,
      patientBodyMeasurementsRef?.edges ?? null,
    )?.toSorted((a, b) => {
      return Number(new Date(b.node.date)) - Number(new Date(a.node.date))
    }) ?? null
  const patientBodyMeasurementsFirstNode = patientBodyMeasurements?.at(0)?.node

  const { verifiedAt, verifiedBy, isVerified } = useGetHealthInformationVerification(
    patientId,
    country,
    "BODY_MEASUREMENT",
  )

  const showAsUnverified = validAppointment && !isVerified && !isLatestData(patientBodyMeasurementsFirstNode?.date)
  const canVerify = validAppointment && !isVerified

  const defaultHeight = latestBodyMeasurement?.height
  const defaultHeightAsString = defaultHeight ? String(defaultHeight) : ""

  const schema = useMemo(() => makeBodyMeasurementSchema(isHeightRequired), [isHeightRequired])

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      date: serverDate,
      weight: "",
      height: defaultHeightAsString,
    },
  })

  const {
    reset,
    formState: { isDirty },
  } = form

  const handleVerifyBodyMeasurement = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "BODY_MEASUREMENT",
      appointmentId: appointmentId!,
    }

    handleVerifyHealthInformation(verification)
  }

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_bodyMeasurements_close")
    closeDrawer()
  }, [closeDrawer])

  useDirtyDrawerMaker(isDirty, handleClose)

  const createOrUpdatePatientBodyMeasurement = useCreateOrUpdatePatientBodyMeasurementMutation(country)

  const connectionID = getBodyMeasurementConnectionId(patientId)
  const existingRecords: Array<string> = (patientBodyMeasurements ?? []).map((bm) => bm.node.date)

  const handleAdd: SubmitHandler<BodyMeasurementSchemaOut> = async (data) => {
    if (existingRecords.includes(data.date.toString()) && !editing) {
      const isConfirmed = await confirm({
        confirmText: "common.button.overwrite",
        cancelText: "common.button.keepOriginalValues",
        title: t("patient.healthInformation.bodyMeasurements.overwriteDialog.title") + "?",
        content: "patient.healthInformation.bodyMeasurements.overwriteDialog.content",
      })
      if (!isConfirmed) return
    }

    const newOrChangedBodyMeasurement: De_createOrUpdatePatientBodyMeasurementInput &
      Ch_createOrUpdatePatientBodyMeasurementInput = {
      patientId,
      height: data.height ? Number.parseInt(data.height) : null,
      weight: Number.parseFloat(data.weight),
      date: data.date.toString(),
    }

    createOrUpdatePatientBodyMeasurement(newOrChangedBodyMeasurement, connectionID, () => {
      const updatedDefaultHeight =
        new Date(newOrChangedBodyMeasurement.date) >=
        new Date((latestBodyMeasurement ?? newOrChangedBodyMeasurement).date)
          ? newOrChangedBodyMeasurement.height
          : latestBodyMeasurement?.height

      reset({
        date: serverDate,
        weight: "",
        height: updatedDefaultHeight ? String(updatedDefaultHeight) : "",
      })

      if (canVerify) {
        handleVerifyBodyMeasurement()
      }

      if (editing) {
        setEditing(null)
      }
    })
  }

  const handleClear = () => {
    setEditing(null)

    reset({
      date: serverDate,
      weight: "",
      height: defaultHeightAsString,
    })
  }

  const handleEdit = (bodyMeasurement: PatientBodyMeasurementsOutputFragment$data[0]) => {
    setEditing(bodyMeasurement)
    const { date, weight, height } = bodyMeasurement.node
    form.reset({
      ...form.getValues(),
      date: new DateOnly(date),
      weight: weight.toString(),
      height: height?.toString() ?? "",
    })
  }

  useEffect(() => {
    setLatestBodyMeasurement(patientBodyMeasurementsFirstNode)
  }, [patientBodyMeasurementsFirstNode])

  useEffect(() => {
    reset({
      date: serverDate,
      weight: "",
      height: defaultHeightAsString,
    })
  }, [defaultHeightAsString, reset])

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent data-testid="health-body-measurements-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="py-4.5 pr-4">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium"
            data-testid="body-measurement-drawer-title">
            {t("patient.healthInformation.bodyMeasurements.title")}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="h-full p-0">
          <ScrollWrapper className="scrollbar-thumb-rounded-full scrollbar-w-1 hover:scrollbar-thumb-surface-pressed scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent p-4">
            <div className="flex flex-col">
              {isEditMode && (
                <FormProvider {...form}>
                  <BodyMeasurementForm
                    isEditing={!!editing}
                    isHeightRequired={isHeightRequired}
                    serverDate={serverDate}
                    onSubmit={handleAdd}
                    onClear={handleClear}
                  />
                </FormProvider>
              )}
              {patientBodyMeasurements && patientBodyMeasurements.length > 0 && (
                <BodyMeasurementTable
                  isEditMode={isEditMode}
                  className={isEditMode ? "pt-4" : ""}
                  bodyMeasurements={patientBodyMeasurements}
                  onEdit={handleEdit}
                  onDelete={handleClear}
                  showAsUnverified={showAsUnverified}
                  onVerify={canVerify ? handleVerifyBodyMeasurement : undefined}
                  editing={editing ?? null}
                />
              )}
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden">
          {!!verifiedBy && !!verifiedAt && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button variant="secondary" onClick={handleClose} data-testid="close-button">
            {t("common.button.close")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export { BodyMeasurementsDrawer }
